import { Component, input, output, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { IconComponent } from '../../../icons/icon.component';
import type { NavIconName } from '../../../icons/nav-icon.model';

/**
 * Sidebar Navigation Item
 */
export interface SidebarItem {
  id: string;
  label: string;
  icon?: NavIconName;
  route?: string;
  badge?: string | number;
  children?: SidebarItem[];
  roles?: string[];
  disabled?: boolean;
  /** Quando true, destaca o item só na rota exata (ex.: painel `/inicio/comissoes`). */
  exact?: boolean;
}

/**
 * Sidebar Component
 * 
 * Persistent navigation sidebar with collapsible sections, active state indication,
 * and keyboard navigation support.
 * 
 * @example
 * ```typescript
 * // In your component
 * items: SidebarItem[] = [
 *   {
 *     id: 'portfolio',
 *     label: 'Portfólio',
 *     icon: '📊',
 *     route: '/portfolio',
 *     badge: 3
 *   },
 *   {
 *     id: 'gestao',
 *     label: 'Gestão',
 *     icon: '📁',
 *     children: [
 *       { id: 'programas', label: 'Programas', route: '/programas' },
 *       { id: 'projetos', label: 'Projetos', route: '/projetos' }
 *     ]
 *   }
 * ];
 * ```
 * 
 * ```html
 * <mg3p-sidebar
 *   [items]="items"
 *   [collapsed]="false"
 *   [userRole]="currentUserRole"
 *   (itemClick)="onItemClick($event)"
 *   (collapseToggle)="onCollapseToggle($event)"
 * />
 * ```
 * 
 * Features:
 * - Collapsible sidebar (desktop)
 * - Expandable sections
 * - Active route highlighting
 * - Badge support
 * - Role-based visibility
 * - Keyboard navigation (Tab, Enter, Space, Arrow keys)
 * - Mobile responsive (drawer)
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent],
  template: `
    <aside
      class="sidebar"
      [class.sidebar--collapsed]="collapsed()"
      [class.sidebar--mobile-open]="mobileOpen()"
      [attr.aria-label]="ariaLabel()"
      role="navigation"
    >
      <!-- Header -->
      <div class="sidebar__header">
        @if (!collapsed()) {
          <div class="sidebar__logo">
            <img src="/assets/pmdf-logo.png" alt="PMDF Logo" class="sidebar__logo-img" />
            <span class="sidebar__logo-text">{{ title() }}</span>
          </div>
        }
        
        <!-- Desktop collapse toggle -->
        <button
          type="button"
          class="sidebar__toggle"
          [class.sidebar__toggle--collapsed]="collapsed()"
          (click)="toggleCollapse()"
          [attr.aria-label]="collapsed() ? 'Expandir menu' : 'Recolher menu'"
          [attr.aria-expanded]="!collapsed()"
        >
          <mg3p-icon [name]="collapsed() ? 'chevron-right' : 'chevron-left'" size="sm" />
        </button>
      </div>

      <!-- Navigation -->
      <nav class="sidebar__nav">
        <ul class="sidebar__list" role="list">
          @for (item of visibleItems(); track item.id) {
            <li class="sidebar__item">
              @if (item.children && item.children.length > 0) {
                <!-- Parent item with children -->
                <button
                  type="button"
                  class="sidebar__link sidebar__link--parent"
                  [class.sidebar__link--expanded]="isExpanded(item.id)"
                  [class.sidebar__link--disabled]="item.disabled"
                  (click)="toggleSection(item.id)"
                  [attr.aria-expanded]="isExpanded(item.id)"
                  [attr.aria-controls]="'section-' + item.id"
                  [disabled]="item.disabled"
                >
                  @if (item.icon) {
                    <span class="sidebar__icon-wrap"><mg3p-icon [name]="item.icon" size="md" /></span>
                  }
                  @if (!collapsed()) {
                    <span class="sidebar__label">{{ item.label }}</span>
                    <span class="sidebar__expand-icon" aria-hidden="true">
                      <mg3p-icon [name]="isExpanded(item.id) ? 'chevron-down' : 'chevron-right'" size="sm" />
                    </span>
                  }
                </button>

                <!-- Children -->
                @if (isExpanded(item.id) && !collapsed()) {
                  <ul
                    class="sidebar__sublist"
                    [id]="'section-' + item.id"
                    role="list"
                  >
                    @for (child of item.children; track child.id) {
                      @if (isItemVisible(child)) {
                        <li class="sidebar__item sidebar__item--child">
                          <a
                            [routerLink]="child.route"
                            routerLinkActive="sidebar__link--active"
                            [routerLinkActiveOptions]="{ exact: child.exact ?? false }"
                            class="sidebar__link sidebar__link--child"
                            [class.sidebar__link--disabled]="child.disabled"
                            (click)="onItemClicked(child, $event)"
                            [attr.aria-current]="isActive(child.route, child.exact) ? 'page' : null"
                            [attr.aria-disabled]="child.disabled"
                          >
                            @if (child.icon) {
                              <span class="sidebar__icon-wrap"><mg3p-icon [name]="child.icon" size="sm" /></span>
                            }
                            <span class="sidebar__label">{{ child.label }}</span>
                            @if (child.badge) {
                              <span class="sidebar__badge" aria-label="{{ child.badge }} itens">
                                {{ child.badge }}
                              </span>
                            }
                          </a>
                        </li>
                      }
                    }
                  </ul>
                }
              } @else {
                <!-- Single item without children -->
                <a
                  [routerLink]="item.route"
                  routerLinkActive="sidebar__link--active"
                  class="sidebar__link"
                  [class.sidebar__link--disabled]="item.disabled"
                  (click)="onItemClicked(item, $event)"
                  [attr.aria-current]="isActive(item.route) ? 'page' : null"
                  [attr.aria-disabled]="item.disabled"
                  [title]="collapsed() ? item.label : ''"
                >
                  @if (item.icon) {
                    <span class="sidebar__icon-wrap"><mg3p-icon [name]="item.icon" size="md" /></span>
                  }
                  @if (!collapsed()) {
                    <span class="sidebar__label">{{ item.label }}</span>
                    @if (item.badge) {
                      <span class="sidebar__badge" aria-label="{{ item.badge }} itens">
                        {{ item.badge }}
                      </span>
                    }
                  }
                </a>
              }
            </li>
          }
        </ul>
      </nav>

      @if (visibleBottomItems().length > 0) {
        <nav class="sidebar__bottom-nav" aria-label="Módulos complementares">
          <ul class="sidebar__list" role="list">
            @for (item of visibleBottomItems(); track item.id) {
              <li class="sidebar__item">
                <a
                  [routerLink]="item.route"
                  routerLinkActive="sidebar__link--active"
                  class="sidebar__link"
                  [class.sidebar__link--disabled]="item.disabled"
                  (click)="onItemClicked(item, $event)"
                  [attr.aria-current]="isActive(item.route) ? 'page' : null"
                  [attr.aria-disabled]="item.disabled"
                  [title]="collapsed() ? item.label : ''"
                >
                  @if (item.icon) {
                    <span class="sidebar__icon-wrap"><mg3p-icon [name]="item.icon" size="md" /></span>
                  }
                  @if (!collapsed()) {
                    <span class="sidebar__label">{{ item.label }}</span>
                    @if (item.badge) {
                      <span class="sidebar__badge" aria-label="{{ item.badge }} itens">
                        {{ item.badge }}
                      </span>
                    }
                  }
                </a>
              </li>
            }
          </ul>
        </nav>
      }

      <!-- Footer (optional) -->
      @if (showFooter()) {
        <div class="sidebar__footer">
          <ng-content select="[footer]"></ng-content>
        </div>
      }
    </aside>

    <!-- Mobile overlay -->
    @if (mobileOpen()) {
      <div
        class="sidebar__overlay"
        (click)="closeMobile()"
        aria-hidden="true"
      ></div>
    }
  `,
  styles: [`
    .sidebar {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: var(--sidebar-width, 280px);
      background: var(--shell-chrome-bg, var(--sidebar-bg-bottom));
      border-right: 1px solid var(--shell-chrome-border, var(--sidebar-border));
      color: var(--sidebar-text);
      display: flex;
      flex-direction: column;
      transition: width var(--transition-normal);
      z-index: var(--z-sidebar, 100);
    }

    .sidebar--collapsed {
      width: var(--sidebar-collapsed-width, 64px);
    }

    /* Header */
    .sidebar__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-md);
      border-bottom: 1px solid var(--shell-chrome-border, var(--sidebar-border));
      min-height: 64px;
    }

    .sidebar__logo {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
    }

    .sidebar__logo-img {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }

    .sidebar__logo-text {
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-semibold);
      color: var(--sidebar-text);
    }

    .sidebar__toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      background: color-mix(in srgb, var(--sidebar-text) 8%, transparent);
      border: 1px solid var(--sidebar-border);
      border-radius: var(--radius-sm);
      color: var(--sidebar-text);
      cursor: pointer;
      transition: all var(--transition-fast);
    }

    .sidebar__toggle:hover {
      background: var(--sidebar-hover-bg);
    }

    .sidebar__toggle:focus-visible {
      outline: 2px solid color-mix(in srgb, var(--sidebar-text) 50%, transparent);
      outline-offset: 2px;
    }

    .sidebar__toggle-icon {
      font-size: var(--font-size-base);
      line-height: 1;
    }

    /* Navigation */
    .sidebar__nav {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: var(--spacing-sm) 0;
    }

    .sidebar__bottom-nav {
      flex-shrink: 0;
      margin-top: auto;
      border-top: 1px solid var(--shell-chrome-border, var(--sidebar-border));
      padding: var(--spacing-sm) 0;
    }

    .sidebar__list {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .sidebar__item {
      margin: 0;
    }

    .sidebar__link {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      padding: var(--spacing-sm) var(--spacing-md);
      color: var(--sidebar-text-muted);
      text-decoration: none;
      transition: all var(--transition-fast);
      cursor: pointer;
      border: none;
      background: transparent;
      width: 100%;
      text-align: left;
      font-family: var(--font-pmdf-sans, inherit);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      line-height: var(--line-height-body, 1.5);
      position: relative;
    }

    .sidebar__link:hover:not(.sidebar__link--disabled) {
      background: var(--sidebar-hover-bg);
      color: var(--sidebar-text);
    }

    .sidebar__link:focus-visible {
      outline: 2px solid color-mix(in srgb, var(--sidebar-text) 45%, transparent);
      outline-offset: -2px;
    }

    .sidebar__link--active {
      background: var(--sidebar-active-bg);
      color: var(--sidebar-text);
      font-weight: var(--font-weight-semibold);
    }

    .sidebar__link--active::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: var(--sidebar-text);
    }

    .sidebar__link--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .sidebar__link--parent {
      font-weight: var(--font-weight-medium);
    }

    .sidebar__link--child {
      padding-left: calc(var(--spacing-md) + var(--spacing-lg));
      font-size: var(--font-size-sm);
    }

    .sidebar__icon-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      flex-shrink: 0;
      opacity: 0.92;
    }

    .sidebar__link--active .sidebar__icon-wrap {
      opacity: 1;
    }

    .sidebar__label {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .sidebar__expand-icon {
      display: flex;
      align-items: center;
      margin-left: auto;
      opacity: 0.85;
    }

    .sidebar__link--expanded .sidebar__expand-icon {
      transform: rotate(0deg);
    }

    .sidebar__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      padding: 0 var(--spacing-xs);
      background: color-mix(in srgb, var(--sidebar-text) 22%, transparent);
      color: var(--sidebar-text);
      border-radius: var(--radius-full);
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-semibold);
    }

    /* Sublist */
    .sidebar__sublist {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    /* Footer */
    .sidebar__footer {
      padding: var(--spacing-md);
      border-top: 1px solid var(--shell-chrome-border, var(--sidebar-border));
    }

    /* Collapsed state */
    .sidebar--collapsed .sidebar__logo-text,
    .sidebar--collapsed .sidebar__label,
    .sidebar--collapsed .sidebar__expand-icon,
    .sidebar--collapsed .sidebar__badge {
      display: none;
    }

    .sidebar--collapsed .sidebar__link {
      justify-content: center;
      padding: var(--spacing-sm);
    }

    .sidebar--collapsed .sidebar__link--child {
      padding-left: var(--spacing-sm);
    }

    /* Mobile */
    @media (max-width: 768px) {
      .sidebar {
        transform: translateX(-100%);
        transition: transform var(--transition-normal);
      }

      .sidebar--mobile-open {
        transform: translateX(0);
      }

      .sidebar__toggle {
        display: none;
      }

      .sidebar__overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: calc(var(--z-sidebar) - 1);
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .sidebar,
      .sidebar__link,
      .sidebar__toggle,
      .sidebar__expand-icon {
        transition: none;
      }
    }
  `]
})
export class SidebarComponent {
  // Inputs
  items = input.required<SidebarItem[]>();
  bottomItems = input<SidebarItem[]>([]);
  collapsed = input<boolean>(false);
  title = input<string>('MG3P');
  userRole = input<string>('');
  ariaLabel = input<string>('Menu de navegação principal');
  showFooter = input<boolean>(false);

  // Outputs
  itemClick = output<SidebarItem>();
  collapseToggle = output<boolean>();

  // State
  expandedSections = signal<Set<string>>(new Set());
  mobileOpen = signal<boolean>(false);
  currentRoute = signal<string>('');

  // Computed
  visibleItems = computed(() => {
    return this.items().filter(item => this.isItemVisible(item));
  });

  visibleBottomItems = computed(() => {
    return this.bottomItems().filter(item => this.isItemVisible(item));
  });

  constructor(private router: Router) {
    // Track current route
    this.currentRoute.set(this.router.url);
    
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.currentRoute.set(event.url);
      });

    // Auto-expand active sections
    effect(() => {
      const route = this.currentRoute();
      const items = this.items();
      
      const allItems = [...items, ...this.bottomItems()];
      allItems.forEach(item => {
        if (item.children) {
          const hasActiveChild = item.children.some(
            (child) => child.route && this.isActive(child.route, child.exact),
          );
          const onSection = item.route && this.isActive(item.route, item.exact);
          const prefix = this.sectionRoutePrefix(item);
          const inSection = prefix ? route.startsWith(prefix) : false;
          if (hasActiveChild || onSection || inSection) {
            this.expandedSections.update(set => {
              const newSet = new Set(set);
              newSet.add(item.id);
              return newSet;
            });
          }
        } else if (item.route && this.isActive(item.route, item.exact)) {
          // Mantém seções irmãs recolhidas; filhos tratados acima.
        }
      });
    });
  }

  toggleCollapse(): void {
    const newState = !this.collapsed();
    this.collapseToggle.emit(newState);
  }

  toggleSection(sectionId: string): void {
    this.expandedSections.update(set => {
      const newSet = new Set(set);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }

  isExpanded(sectionId: string): boolean {
    return this.expandedSections().has(sectionId);
  }

  isActive(route?: string, exact = false): boolean {
    if (!route) return false;
    const current = this.currentRoute().split('?')[0];
    if (exact) {
      return current === route || current === `${route}/`;
    }
    return current.startsWith(route);
  }

  private sectionRoutePrefix(item: SidebarItem): string | null {
    if (!item.children?.length) {
      return null;
    }
    const routes = item.children.map((c) => c.route).filter((r): r is string => !!r);
    if (routes.length === 0) {
      return null;
    }
    let prefix = routes[0];
    for (const r of routes.slice(1)) {
      while (!r.startsWith(prefix) && prefix.length > 0) {
        const lastSlash = prefix.lastIndexOf('/');
        prefix = lastSlash > 0 ? prefix.slice(0, lastSlash) : '';
      }
    }
    return prefix || null;
  }

  isItemVisible(item: SidebarItem): boolean {
    if (item.disabled) return false;
    if (!item.roles || item.roles.length === 0) return true;
    return item.roles.includes(this.userRole());
  }

  onItemClicked(item: SidebarItem, event: Event): void {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    
    this.itemClick.emit(item);
    
    // Close mobile menu on navigation
    if (window.innerWidth <= 768) {
      this.closeMobile();
    }
  }

  openMobile(): void {
    this.mobileOpen.set(true);
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
  }
}

// Made with Bob
